import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Spin, Alert, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from '../store/ReduxStore';
import { exchangeSSOCode, getSsoState, clearSsoState } from '../actions/user';
import { AsyncDispatch } from '../../types/global';
import LoginFooter from './LoginFooter';

const SSO_CODE_PROCESSED_KEY = 'sso_code_processed';

const AuthCallbackPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch: AsyncDispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    const errorDescription = searchParams.get('error_description');

    if (errorParam) {
      setError(errorDescription || errorParam);
      clearSsoState();
      return;
    }

    if (!code) {
      setError(t('login:ssoMissingCode'));
      clearSsoState();
      return;
    }

    // Validate state for CSRF protection (if state was stored)
    const savedState = getSsoState();
    if (savedState && state !== savedState) {
      setError(t('login:ssoInvalidState'));
      clearSsoState();
      return;
    }

    // Check if this code was already processed (prevents duplicate calls on remount)
    const processedCode = sessionStorage.getItem(SSO_CODE_PROCESSED_KEY);
    if (processedCode === code) {
      return;
    }

    // Mark this code as being processed
    sessionStorage.setItem(SSO_CODE_PROCESSED_KEY, code);

    const handleCodeExchange = async () => {
      try {
        // @ts-ignore
        await dispatch(exchangeSSOCode(code));
        // Clear the processed code on success
        sessionStorage.removeItem(SSO_CODE_PROCESSED_KEY);
        navigate('/');
      } catch (err: any) {
        // Clear on error so user can retry
        sessionStorage.removeItem(SSO_CODE_PROCESSED_KEY);
        clearSsoState();
        const errorMessage = err.response?.data?.error || err.message || t('login:ssoError');
        setError(errorMessage);
      }
    };

    handleCodeExchange();
  }, [searchParams, dispatch, navigate, t]);

  const handleBackToLogin = () => {
    navigate('/');
  };

  if (error) {
    return (
      <div className="login__background">
        <div className="login__box">
          <div className="login__box-header">
            <div className="login-logo" />
          </div>
          <div className="login__box-content">
            <div className="login--header-content">
              <div className="login__title">{t('login:ssoErrorTitle')}</div>
            </div>
            <Alert type="error" showIcon message={error} style={{ marginBottom: 20 }} />
            <Button type="primary" size="large" block onClick={handleBackToLogin}>
              {t('login:backToLogin')}
            </Button>
          </div>
        </div>
        <LoginFooter />
      </div>
    );
  }

  return (
    <div className="login__background">
      <div className="login__box">
        <div className="login__box-header">
          <div className="login-logo" />
        </div>
        <div className="login__box-content">
          <div className="login--header-content">
            <div className="login__title">{t('login:ssoCompleting')}</div>
          </div>
          <div className="flex justify-center py-8">
            <Spin size="large" />
          </div>
        </div>
      </div>
      <LoginFooter />
    </div>
  );
};

export default AuthCallbackPage;
